import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenView from '../components/shared/FullScreenView';
import FullScreenTagPanel from '../components/admin/FullScreenTagPanel';
import ArtistStatusPanel from '../components/admin/ArtistStatusPanel';
import StatusMessage, { type FetchState } from '../components/shared/StatusMessage';
import ConfirmModal from '../components/shared/ConfirmModal';
import InlineErrorBanner from '../components/shared/InlineErrorBanner';
import SparklesIcon from '../components/shared/SparklesIcon';
import TagPhotoCard from '../components/shared/TagPhotoCard';
import BatchListView from '../components/admin/BatchListView';
import ApprovalPhotoCard from '../components/admin/ApprovalPhotoCard';
import AutoTagModal, { type AutoTagState } from '../components/admin/AutoTagModal';
import CorrectTagsModal from '../components/admin/CorrectTagsModal';
import ReviewGalleryOpenForm from '../components/admin/ReviewGalleryOpenForm';
import SecondaryBatchDetail from '../components/admin/SecondaryBatchDetail';
import ApprovalTypeBadge from '../components/shared/ApprovalTypeBadge';
import { getStatusBadge } from '../utils/batchStatus';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { downloadExport, type ExportProgress } from '../utils/downloadExport';
import ExportProgressBar from '../components/shared/ExportProgressBar';
import { useBatch } from '../context/BatchContext';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { creditUserName, creditArtistId } from '../utils/photoCredits';
import type {
    Photo, Batch, User, CompoundTag, ArtistStatus, ApprovalStatus, FinalStatus, ApiErrorBody, GalleryRelease,
} from '../types';

// ── Types ────────────────────────────────────────────────────────────────────

type ActiveView = null | 'tagging' | 'approval' | 'secondary-review';
type Filter = 'all' | 'tagged' | 'untagged';

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildTagMap = (photos: Photo[]): Map<number, string> => {
    const map = new Map<number, string>();
    photos.forEach(photo => {
        if (!photo.isGroupPhoto && (photo.credits?.length ?? 0) > 0) {
            const names = (photo.credits ?? [])
                .map(c => creditUserName(c))
                .filter((n): n is string => Boolean(n))
                .join(' ');
            if (names) map.set(photo.id, names);
        }
    });
    return map;
};

// ── Main component ────────────────────────────────────────────────────────────

export default function Photos() {
    const navigate = useNavigate();
    const { batches, setBatches, batchId, setBatchId, selectedBatch, refreshBatches } = useBatch();

    // null = batch list, 'tagging' = tag view, 'approval' = approval view
    const [activeView, setActiveView] = useState<ActiveView>(null);

    // Shared
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [inlineError, setInlineError] = useState('');
    const [showReviewGalleryForm, setShowReviewGalleryForm] = useState(false);
    const [reviewBatchIds, setReviewBatchIds] = useState<Set<number>>(new Set());
    const { modal, showConfirm, showChoice } = useConfirmModal();
    

    // Tagging state
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [tagMap, setTagMap] = useState<Map<number, string>>(new Map());
    const [activePhotoId, setActivePhotoId] = useState<number | null>(null);
    const [saveStatus, setSaveStatus] = useState('');
    const [filter, setFilter] = useState<Filter>('all');
    const [tagPanelOpen, setTagPanelOpen] = useState(true);
    const [artists, setArtists] = useState<User[]>([]);
    const [taggingGroups, setTaggingGroups] = useState<{ id: number; name: string }[]>([]);
    const [tagHistory, setTagHistory] = useState<CompoundTag[]>([]);
    const [artistQuery, setArtistQuery] = useState('');
    const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
    const [tagName, setTagName] = useState('');
    const [savingTag, setSavingTag] = useState(false);

    // Auto-tag state
    const [autoTag, setAutoTag] = useState<AutoTagState | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [correctTagsPhoto, setCorrectTagsPhoto] = useState<Photo | null>(null);
    const cancelledRef = useRef(false);
    const pausePromiseRef = useRef<Promise<void> | null>(null);
    const pauseResolveRef = useRef<(() => void) | null>(null);

    // Approval state
    const [artistStatus, setArtistStatus] = useState<ArtistStatus[]>([]);
    const [photoCreditsMap, setPhotoCreditsMap] = useState<Map<string, ApprovalStatus>>(new Map());
    const [photoStatusMap, setPhotoStatusMap] = useState<Map<number, FinalStatus>>(new Map());
    const [loadState, setLoadState] = useState<FetchState>({ status: 'ready', error: null });
    const [committing, setCommitting] = useState(false);
    const [exportingStatus, setExportingStatus] = useState<string | null>(null);
    const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
    const [exportMode, setExportMode] = useState(false);
    const [correctTagsMode, setCorrectTagsMode] = useState(false);
    const [exportSelectedIds, setExportSelectedIds] = useState<Set<number>>(new Set());
    const committedRef = useRef(false);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Gallery release state
    const [releases, setReleases] = useState<GalleryRelease[]>([]);
    const [allGroups, setAllGroups] = useState<{ id: number; name: string }[]>([]);
    const [releaseDropdownOpen, setReleaseDropdownOpen] = useState(false);
    const [releasingGroupId, setReleasingGroupId] = useState<number | null>(null);
    const [revokingGroupId, setRevokingGroupId] = useState<number | null>(null);

    const batchIsCommitted = !!selectedBatch && (
        selectedBatch.approvalType === 'single_point'
            ? !['uploaded', 'empty'].includes(selectedBatch.status)
            : !['uploaded', 'ad_selected', 'empty'].includes(selectedBatch.status)
    );

    const artistStatusMap = useMemo(
        () => new Map(artistStatus.map(a => [a.userId, a])),
        [artistStatus]
    );

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (activeView === null) refreshBatches();
    }, [activeView, refreshBatches]);

    useEffect(() => {
        Promise.all([
            apiFetch('/api/users?role=artist', { headers: authHeaders() }).then(r => r.json()),
            apiFetch('/api/compound-tags', { headers: authHeaders() }).then(r => r.json()),
            apiFetch('/api/groups', { headers: authHeaders() }).then(r => r.json()),
        ]).then(([users, tags, grps]: [unknown, unknown, unknown]) => {
            setArtists(Array.isArray(users) ? users : []);
            setTagHistory(Array.isArray(tags) ? tags : (tags as { tags?: CompoundTag[] }).tags ?? []);
            const typed = Array.isArray(grps)
                ? (grps as { id: number; name: string; includeInTagging: boolean }[])
                : [];
            setAllGroups(typed.map(g => ({ id: g.id, name: g.name })));
            setTaggingGroups(typed.filter(g => g.includeInTagging).map(g => ({ id: g.id, name: g.name })));
        }).catch(() => {});
    }, []);

    // Load photos for tagging (isSelected only, except secondary batches)
    useEffect(() => {
        if (!batchId || activeView !== 'tagging') return;
        const isSecondary = batches.find(b => String(b.id) === batchId)?.batchType === 'secondary';
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                const isZeroSelection = batches.find(b => String(b.id) === batchId)?.adZeroSelection === true;
                const loaded = (Array.isArray(data) ? data : []).filter(p => isSecondary || isZeroSelection || p.isSelected);
                setPhotos(loaded);
                setTagMap(buildTagMap(loaded));
            })
            .catch(() => {});
    }, [batchId, activeView, batches]);

    // Load all photos for approval view
    useEffect(() => {
        if (!batchId || activeView !== 'approval') return;
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                setPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err instanceof Error ? err.message : String(err) }));
    }, [batchId, activeView]);

    // Load gallery releases for processed single_point batches
    useEffect(() => {
        if (!batchId || activeView !== 'approval' || !selectedBatch || selectedBatch.approvalType !== 'single_point' || selectedBatch.status !== 'processed') {
            setReleases([]);
            return;
        }
        apiFetch(`/api/batches/${batchId}/releases`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: GalleryRelease[]) => setReleases(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [batchId, activeView, selectedBatch]);

    // Deselect photos that fall outside the active filter
    useEffect(() => {
        if (activeView !== 'tagging') return;
        setSelectedIds(prev => {
            const next = new Set([...prev].filter(id => {
                const photo = photos.find(p => p.id === id);
                if (!photo) return false;
                const isTagged = tagMap.has(photo.id) || photo.isGroupPhoto;
                if (filter === 'tagged') return isTagged;
                if (filter === 'untagged') return !isTagged;
                return true;
            }));
            return next.size === prev.size ? prev : next;
        });
    }, [filter, photos, tagMap, activeView]);

    const fetchArtistStatus = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/artist-status`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: ArtistStatus[]) => setArtistStatus(Array.isArray(data) ? data : []))
            .catch(err => console.error('artist-status fetch failed:', err instanceof Error ? err.message : String(err)));
    }, [batchId]);

    const fetchPhotoCredits = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photo-credits`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: { credits?: { photoId: number; userId: number; approvalStatus: ApprovalStatus }[]; photoStatuses?: { photoId: number; finalStatus: FinalStatus }[] }) => {
                const credits = data.credits ?? [];
                const statuses = data.photoStatuses ?? [];
                const credMap = new Map<string, ApprovalStatus>();
                for (const row of credits) credMap.set(`${row.photoId}-${row.userId}`, row.approvalStatus);
                setPhotoCreditsMap(credMap);
                const statusMap = new Map<number, FinalStatus>();
                for (const row of statuses) statusMap.set(row.photoId, row.finalStatus);
                setPhotoStatusMap(statusMap);
            })
            .catch(err => console.error('photo-credits fetch failed:', err instanceof Error ? err.message : String(err)));
    }, [batchId]);

    // Graduated polling for approval view.
    // If committedRef is true (just committed), uses fast schedule:
    //   5s × 3, 10s × 2, 15s × 2, 30s × 2, then 60s indefinitely.
    // Reasoning: email bounces can arrive within seconds; the old 60s poll
    // would leave a failed email showing "sent" for almost a full minute.
    useEffect(() => {
        if (activeView !== 'approval' || !batchId) return;

        const schedule = committedRef.current
            ? [5000, 5000, 5000, 10000, 10000, 15000, 15000, 30000, 30000]
            : [];
        let idx = 0;

        fetchArtistStatus();
        fetchPhotoCredits();

        const tick = () => {
            fetchArtistStatus();
            fetchPhotoCredits();
            const delay = idx < schedule.length ? schedule[idx++] : 60000;
            pollTimerRef.current = setTimeout(tick, delay);
        };

        const firstDelay = idx < schedule.length ? schedule[idx++] : 60000;
        pollTimerRef.current = setTimeout(tick, firstDelay);

        return () => {
            clearTimeout(pollTimerRef.current);
            committedRef.current = false;
        };
    }, [activeView, batchId, fetchArtistStatus, fetchPhotoCredits]);

    // ── State reset helpers ───────────────────────────────────────────────────

    const resetTaggingState = () => {
        setPhotos([]);
        setTagMap(new Map());
        setActivePhotoId(null);
        setFilter('all');
        setSelectedIds(new Set());
        setSelectedArtists([]);
        setArtistQuery('');
        setSaveStatus('');
        setTagPanelOpen(true);
    };

    const resetApprovalState = () => {
        setArtistStatus([]);
        setPhotoCreditsMap(new Map());
        setPhotoStatusMap(new Map());
        setLoadState({ status: 'ready', error: null });
        setCommitting(false);
        setExportingStatus(null);
        setExportMode(false);
        setExportSelectedIds(new Set());
    };

    // ── Batch list handlers ───────────────────────────────────────────────────

    const handleSelectBatch = (batch: Batch) => {
        setInlineError('');
        if (batch.batchType === 'secondary') {
            if (batch.status === 'ad_selected') {
                setBatchId(String(batch.id));
                resetTaggingState();
                setActiveView('tagging');
            } else if (batch.status === 'pending' || batch.status === 'processed') {
                setBatchId(String(batch.id));
                committedRef.current = false;
                resetApprovalState();
                setPhotos([]);
                setActiveView('approval');
            } else {
                // review_open or uploaded — show the secondary review panel
                setBatchId(String(batch.id));
                setActiveView('secondary-review');
            }
            return;
        }
        if (batch.status === 'uploaded' || batch.status === 'empty') {
            navigate(`/admin/upload/${batch.id}`);
        } else if (batch.status === 'ad_selected') {
            setBatchId(String(batch.id));
            if (batch.approvalType === 'single_point') {
                committedRef.current = false;
                resetApprovalState();
                setPhotos([]);
                setActiveView('approval');
            } else {
                resetTaggingState();
                setActiveView('tagging');
            }
        } else {
            setBatchId(String(batch.id));
            committedRef.current = false;
            resetApprovalState();
            setPhotos([]);
            setActiveView('approval');
        }
    };

    const handleBack = () => {
        clearTimeout(pollTimerRef.current);
        setBatchId('');
        setInlineError('');
        resetTaggingState();
        resetApprovalState();
        setActiveView(null);
    };

    // ── Tagging handlers ──────────────────────────────────────────────────────

    const togglePhoto = (photoId: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
            return next;
        });
    };

    const toggleArtist = (artistId: string | number, checked: boolean) => {
        setSelectedArtists(prev =>
            checked ? [...prev, String(artistId)] : prev.filter(id => id !== String(artistId))
        );
        if (checked) setArtistQuery('');
    };

    const refreshPhotos = () => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                const loaded = (Array.isArray(data) ? data : []).filter(p => p.isSelected);
                const updatedMap = new Map(loaded.map(p => [p.id, p]));
                setPhotos(prev => prev.map(p => updatedMap.get(p.id) ?? p));
                setTagMap(buildTagMap(loaded));
            })
            .catch(() => {});
    };

    const refreshTagHistory = () => {
        apiFetch('/api/compound-tags', { headers: authHeaders() })
            .then(r => r.json())
            .then((data: CompoundTag[] | { tags?: CompoundTag[] }) => setTagHistory(Array.isArray(data) ? data : data.tags ?? []))
            .catch(() => {});
    };

    const handleDeleteTag = async (tagId: number) => {
        try {
            const res = await apiFetch(`/api/compound-tags/${tagId}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete tag');
            setTagHistory(prev => prev.filter(t => t.id !== tagId));
        } catch {
            setInlineError('Failed to remove tag.');
        }
    };

    const handleSaveTags = async () => {
        if (selectedIds.size === 0 || selectedArtists.length === 0) return;

        const photosToTag = [...selectedIds]
            .map(id => photos.find(p => p.id === id))
            .filter((p): p is Photo => Boolean(p));

        const tagRequests = photosToTag.map(photo => {
            const existingIds = (photo.credits ?? [])
                .filter(c => c.userId)
                .map(c => creditArtistId(c.userId));
            const merged = [...new Set([...existingIds, ...selectedArtists])];
            return { photoId: photo.id, mergedIds: merged, hasExisting: existingIds.length > 0 };
        });

        const overLimit = tagRequests.filter(req => req.mergedIds.length > 2);

        if (overLimit.length > 0) {
            const n = overLimit.length;
            const groupIds = new Set(overLimit.map(req => req.photoId));
            const individualRequests = tagRequests.filter(req => !groupIds.has(req.photoId));

            const allFresh = overLimit.every(req => !req.hasExisting);

            if (allFresh) {
                // No existing tags to replace — skip the three-button modal
                const ok = await showConfirm({
                    title: 'Tag as Group?',
                    body: `Under the CBA, photos with 3+ artists are treated as group photos and bypass individual artist approval.`,
                    confirmLabel: 'Tag as Group',
                });
                if (!ok) return;

                await Promise.all([...groupIds].map(photoId =>
                    apiFetch(`/api/photos/${photoId}/group`, {
                        method: 'PATCH',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ isGroupPhoto: true }),
                    })
                ));
            } else {
                const choice = await showChoice({
                    title: 'Too Many Tagged Artists',
                    body: `${n} photo${n !== 1 ? 's' : ''} ${n !== 1 ? 'have' : 'has'} existing tags that would bring the total to 3 or more artists. Under the CBA, photos with 3+ artists are treated as group photos and bypass individual artist approval.`,
                    confirmLabel: 'Tag as Group',
                    altConfirmLabel: 'Replace Existing Tags',
                    cancelLabel: 'Cancel',
                });
                if (choice === 'cancel') return;

                if (choice === 'confirm') {
                    await Promise.all([...groupIds].map(photoId =>
                        apiFetch(`/api/photos/${photoId}/group`, {
                            method: 'PATCH',
                            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ isGroupPhoto: true }),
                        })
                    ));
                } else {
                    // 'alt': replace ALL selected photos' tags with just the current selection
                    await Promise.all([...selectedIds].map(photoId =>
                        apiFetch(`/api/photos/${photoId}/tags`, {
                            method: 'PATCH',
                            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ artistIds: selectedArtists }),
                        })
                    ));
                }
            }

            if (individualRequests.length > 0) {
                await Promise.all(individualRequests.map(({ photoId, mergedIds }) =>
                    apiFetch(`/api/photos/${photoId}/tags`, {
                        method: 'PATCH',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ artistIds: mergedIds }),
                    })
                ));
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
            refreshTagHistory();
            setSelectedIds(new Set());
            setSelectedArtists([]);
            setArtistQuery('');
            refreshPhotos();
            return;
        }

        const results = await Promise.all(tagRequests.map(({ photoId, mergedIds }) =>
            apiFetch(`/api/photos/${photoId}/tags`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ artistIds: mergedIds }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed to tag.`);

        if (results.some(r => r.ok)) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
            refreshTagHistory();
        }

        setSelectedIds(new Set());
        setSelectedArtists([]);
        setArtistQuery('');
        refreshPhotos();
    };

    const handleClearTags = async () => {
        if (selectedIds.size === 0) return;
        const ok = await showConfirm({
            title: 'Clear Tags',
            body: `Remove tags from ${selectedIds.size} photo${selectedIds.size !== 1 ? 's' : ''}?`,
            confirmLabel: 'Clear Tags',
            isDestructive: true,
        });
        if (!ok) return;

        const results = await Promise.all([...selectedIds].map(photoId =>
            apiFetch(`/api/photos/${photoId}/tags`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ artistIds: [] }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed to clear.`);
        if (results.some(r => r.ok)) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        }
        setSelectedIds(new Set());
        setSelectedArtists([]);
        refreshPhotos();
    };

    const handleFsConfirmClear = () => showConfirm({
        title: 'Clear Tags',
        body: 'Remove all tags from this photo?',
        confirmLabel: 'Clear Tags',
        isDestructive: true,
    });

    const handleMarkAsGroup = async () => {
        if (selectedIds.size === 0) return;
        const ok = await showConfirm({
            title: 'Tag as Group Photos?',
            body: `Tag ${selectedIds.size} photo${selectedIds.size !== 1 ? 's' : ''} as group? This will delete all individual artist tags on those photos.`,
            confirmLabel: 'Tag as Group',
        });
        if (!ok) return;

        const results = await Promise.all([...selectedIds].map(photoId =>
            apiFetch(`/api/photos/${photoId}/group`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ isGroupPhoto: true }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed.`);
        setSelectedIds(new Set());
        refreshPhotos();
    };

    const handleSaveTag = async () => {
        if (!tagName.trim() || selectedArtists.length === 0) return;
        setSavingTag(true);
        try {
            const res = await apiFetch('/api/compound-tags', {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: tagName.trim(), artistIds: selectedArtists }),
            });
            if (!res.ok) {
                const data: ApiErrorBody = await res.json().catch(() => ({}));
                setInlineError(data.message || 'Failed to save tag');
                return;
            }
            setTagName('');
            setSelectedArtists([]);
            refreshTagHistory();
        } catch {
            setInlineError('Failed to save tag');
        } finally {
            setSavingTag(false);
        }
    };

    const handleCommit = async () => {
        if (!selectedBatch || !batchId) return;
        const ok = await showConfirm({
            title: 'Commit Batch?',
            body: `Commit "${selectedBatch.galleryName} — ${selectedBatch.year}"? This will send approval links to all tagged artists and start the review window. This cannot be undone.`,
            confirmLabel: 'Commit Batch',
        });
        if (!ok) return;
        setCommitting(true);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/commit`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const data: ApiErrorBody = await res.json().catch(() => ({}));
                setInlineError(data.message || 'Failed to commit batch.');
                return;
            }
            setBatches(prev => prev.map(b =>
                String(b.id) === batchId ? { ...b, status: 'pending' } : b
            ));
            committedRef.current = true;
            setActiveView('approval');
        } catch {
            setInlineError('Network error — failed to commit batch.');
        } finally {
            setCommitting(false);
        }
    };

    const handleMarkAsProcessed = async () => {
        if (!batchId) return;
        const isSinglePoint = selectedBatch?.approvalType === 'single_point';
        const ok = await showConfirm({
            title: 'Mark as Processed?',
            body: isSinglePoint
                ? 'This batch will be marked as processed. The approved photos will be finalized.'
                : 'This batch will be marked as processed. No photos will be sent for artist approval.',
            confirmLabel: 'Mark as Processed',
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/batches/${batchId}/close`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const data: ApiErrorBody = await res.json().catch(() => ({}));
                setInlineError(data.message || 'Failed to mark batch as processed.');
                return;
            }
            setBatches(prev => prev.map(b =>
                String(b.id) === batchId ? { ...b, status: 'processed' } : b
            ));
            handleBack();
        } catch {
            setInlineError('Network error — failed to mark batch as processed.');
        }
    };

    // ── Fullscreen single-photo tagging ───────────────────────────────────────

    const handleFsSaveTags = async (photoId: number, artistIds: string[]): Promise<boolean> => {
        if (artistIds.length > 2) {
            const ok = await showConfirm({
                title: 'Tag as Group Photo?',
                body: `This photo would have ${artistIds.length} tagged artists. Under the CBA, photos with 3+ artists are treated as group photos and bypass individual artist approval. Tag it as a group photo instead?`,
                confirmLabel: 'Tag as Group',
            });
            if (!ok) return false;
            return handleFsMarkAsGroup(photoId, { confirmed: true });
        }
        const res = await apiFetch(`/api/photos/${photoId}/tags`, {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ artistIds }),
        });
        if (res.ok) { refreshTagHistory(); refreshPhotos(); }
        return res.ok;
    };

    const handleFsMarkAsGroup = async (photoId: number, { confirmed = false }: { confirmed?: boolean } = {}): Promise<boolean> => {
        if (!confirmed) {
            const ok = await showConfirm({
                title: 'Tag as Group Photo?',
                body: 'Tag this photo as group? This will delete all individual artist tags on this photo.',
                confirmLabel: 'Tag as Group',
            });
            if (!ok) return false;
        }
        const res = await apiFetch(`/api/photos/${photoId}/group`, {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ isGroupPhoto: true }),
        });
        if (res.ok) { refreshTagHistory(); refreshPhotos(); }
        return res.ok;
    };

    const handleFsSaveNamedTag = async (name: string, artistIds: string[]): Promise<boolean> => {
        const res = await apiFetch('/api/compound-tags', {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, artistIds }),
        });
        if (res.ok) refreshTagHistory();
        return res.ok;
    };

    // ── Auto-tag ──────────────────────────────────────────────────────────────

    const runAutoTag = async (selectedPhotos: Photo[]) => {
        cancelledRef.current = false;
        pausePromiseRef.current = null;
        pauseResolveRef.current = null;
        setShowCancelConfirm(false);

        setAutoTag({
            open: true, running: true, done: false,
            total: selectedPhotos.length, currentIndex: 0,
            currentPhoto: selectedPhotos[0], currentStage: 'Starting…',
            tagCloud: new Map(), processed: 0, skipped: 0, groupCount: 0,
        });

        const tagCloudAcc = new Map<string, number>();

        for (let i = 0; i < selectedPhotos.length; i++) {
            if (pausePromiseRef.current) await pausePromiseRef.current;
            if (cancelledRef.current) break;

            const photo = selectedPhotos[i];
            setAutoTag(prev => prev && ({ ...prev, currentIndex: i, currentPhoto: photo, currentStage: 'Fetching image…' }));

            let result: { outcome: string; taggedUsers?: { fName: string; lName: string }[] };
            try {
                setAutoTag(prev => prev && ({ ...prev, currentStage: 'Analyzing faces with AI…' }));
                const res = await apiFetch(`/api/batches/${batchId}/ai-tag/${photo.id}`, {
                    method: 'POST', headers: authHeaders(),
                });
                result = await res.json();
            } catch {
                setAutoTag(prev => prev && ({ ...prev, currentStage: 'Error — skipping' }));
                await new Promise(r => setTimeout(r, 600));
                setAutoTag(prev => prev && ({ ...prev, skipped: (prev.skipped ?? 0) + 1, processed: i + 1 }));
                continue;
            }

            setAutoTag(prev => prev && ({ ...prev, currentStage: 'Saving tags…' }));

            if (result.outcome === 'group') {
                const key = 'Group';
                tagCloudAcc.set(key, (tagCloudAcc.get(key) ?? 0) + 1);
                setAutoTag(prev => prev && ({
                    ...prev,
                    groupCount: (prev.groupCount ?? 0) + 1,
                    tagCloud: new Map(tagCloudAcc),
                    currentStage: 'Tagged as group',
                    processed: i + 1,
                }));
            } else if (result.outcome === 'individual' && (result.taggedUsers?.length ?? 0) > 0) {
                for (const user of result.taggedUsers!) {
                    const name = `${user.fName} ${user.lName.charAt(0)}.`;
                    tagCloudAcc.set(name, (tagCloudAcc.get(name) ?? 0) + 1);
                }
                setAutoTag(prev => prev && ({
                    ...prev,
                    tagCloud: new Map(tagCloudAcc),
                    currentStage: `Tagged: ${result.taggedUsers!.map(u => `${u.fName} ${u.lName.charAt(0)}.`).join(', ')}`,
                    processed: i + 1,
                }));
            } else {
                setAutoTag(prev => prev && ({
                    ...prev,
                    currentStage: result.outcome === 'no_faces' ? 'No faces detected' : 'Skipped',
                    skipped: (prev.skipped ?? 0) + 1,
                    processed: i + 1,
                }));
            }

            await new Promise(r => setTimeout(r, 300));
        }

        setAutoTag(prev => prev && ({
            ...prev,
            running: false,
            done: true,
            currentStage: cancelledRef.current ? 'Cancelled' : 'Complete',
        }));
        setShowCancelConfirm(false);
        refreshPhotos();
        refreshTagHistory();
    };

    const handleStartAutoTag = async () => {
        const selectedPhotos = photos.filter(p => p.isSelected !== false);
        if (selectedPhotos.length === 0) return;
        const hasExisting = selectedPhotos.some(p => (p.credits?.length ?? 0) > 0 || p.isGroupPhoto);
        if (hasExisting) {
            const ok = await showConfirm({
                title: 'Replace Existing Tags?',
                body: 'Some photos already have tags. Running AI tagging will replace all existing tags for every photo in this batch. Continue?',
                confirmLabel: 'Continue',
                isDestructive: true,
            });
            if (!ok) return;
        }
        runAutoTag(selectedPhotos);
    };

    const handleCancelClick = () => {
        if (!autoTag?.running) return;
        let resolve: () => void;
        pausePromiseRef.current = new Promise(r => { resolve = r; });
        pauseResolveRef.current = resolve!;
        setShowCancelConfirm(true);
    };

    const handleCancelConfirm = () => {
        cancelledRef.current = true;
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCancelDeny = () => {
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCloseModal = () => {
        if (autoTag?.running) handleCancelClick();
        else setAutoTag(null);
    };

    // ── Approval handlers ─────────────────────────────────────────────────────

    const retryLoadApprovalPhotos = () => {
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                setPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err instanceof Error ? err.message : String(err) }));
    };

    const runExport = async (status: string, path: string, fallbackFilename: string) => {
        setExportingStatus(status);
        setExportProgress(null);
        try {
            await downloadExport(path, fallbackFilename, setExportProgress);
        } catch (err) {
            setInlineError(err instanceof Error ? err.message : String(err));
        } finally {
            setExportingStatus(null);
            setExportProgress(null);
        }
    };

    const handleExportByStatus = (status: string) =>
        runExport(status, `/api/batches/${batchId}/export?status=${status}`, `${selectedBatch!.galleryName}_${status}.zip`);

    const handleExportSelected = () => {
        if (exportSelectedIds.size === 0) return;
        const ids = Array.from(exportSelectedIds).join(',');
        return runExport('selected', `/api/batches/${batchId}/export?photoIds=${ids}`, `${selectedBatch!.galleryName}_export.zip`);
    };

    const handleResend = useCallback(async (linkId: number) => {
        const res = await apiFetch(`/api/shared-links/${linkId}/resend`, {
            method: 'POST', headers: authHeaders(),
        });
        if (!res.ok) {
            const body: ApiErrorBody = await res.json().catch(() => ({}));
            throw new Error(body.message || `Resend failed (${res.status})`);
        }
        fetchArtistStatus();
        fetchPhotoCredits();
    }, [fetchArtistStatus, fetchPhotoCredits]);

    const handleRelease = async (groupId: number) => {
        if (!batchId) return;
        setReleasingGroupId(groupId);
        setReleaseDropdownOpen(false);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/releases`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ groupId }),
            });
            if (!res.ok) {
                const body: ApiErrorBody = await res.json().catch(() => ({}));
                setInlineError(body.message || `Release failed (${res.status})`);
                return;
            }
            const { data }: { data: GalleryRelease } = await res.json();
            setReleases(prev => [...prev, data]);
        } catch (err) {
            setInlineError(`Release failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setReleasingGroupId(null);
        }
    };

    const handleRevoke = async (groupId: number, groupName: string) => {
        if (!batchId) return;
        const confirmed = await showConfirm({
            title: 'Revoke release',
            body: `Remove gallery access for "${groupName}"? Artists in this group will immediately lose access.`,
            confirmLabel: 'Revoke',
            isDestructive: true,
        });
        if (!confirmed) return;
        setRevokingGroupId(groupId);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/releases/${groupId}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const body: ApiErrorBody = await res.json().catch(() => ({}));
                setInlineError(body.message || `Revoke failed (${res.status})`);
                return;
            }
            setReleases(prev => prev.filter(r => r.groupId !== groupId));
        } catch (err) {
            setInlineError(`Revoke failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setRevokingGroupId(null);
        }
    };

    // ── Derived values ────────────────────────────────────────────────────────

    const taggableArtists = useMemo(() => {
        if (taggingGroups.length === 0) return artists;
        const activeGroupNames = new Set(taggingGroups.map(g => g.name));
        return artists.filter(a =>
            Array.isArray(a.groups) && a.groups.some(gName => activeGroupNames.has(gName))
        );
    }, [artists, taggingGroups]);

    const filteredArtists = artistQuery
        ? taggableArtists.filter(a =>
            `${a.fName} ${a.lName}`.toLowerCase().includes(artistQuery.toLowerCase())
          )
        : [];

    const unselectedMatches = filteredArtists.filter(a => !selectedArtists.includes(String(a.id)));

    const visiblePhotos = photos.filter(photo => {
        const isTagged = tagMap.has(photo.id) || photo.isGroupPhoto;
        if (filter === 'tagged') return isTagged;
        if (filter === 'untagged') return !isTagged;
        return true;
    });

    const activePhoto = activePhotoId ? photos.find(p => p.id === activePhotoId) ?? null : null;
    const activeIdx = activePhoto ? photos.indexOf(activePhoto) : -1;

    const taggedCount   = photos.filter(p => tagMap.has(p.id) || p.isGroupPhoto).length;
    const untaggedCount = photos.length - taggedCount;
    const allTagged     = photos.length > 0 && untaggedCount === 0;
    const isCommitted   = batchIsCommitted;
    const allVisibleSelected = visiblePhotos.length > 0 && visiblePhotos.every(p => selectedIds.has(p.id));
    const someSelectedHaveTags = [...selectedIds].some(id => {
        const photo = photos.find(p => p.id === id);
        return photo && (tagMap.has(photo.id) || photo.isGroupPhoto);
    });

    const approvedCount = photos.filter(p =>
        (photoStatusMap.get(p.id) ?? p.finalStatus) === 'approved'
    ).length;

    const rejectedCount = photos.filter(p =>
        (photoStatusMap.get(p.id) ?? p.finalStatus) === 'rejected'
    ).length;

    const notSelectedCount = photos.filter(p => !p.isSelected).length;

    const hasActiveSecondaryWindow = batches.some(b => b.batchType === 'secondary' && b.status === 'review_open');

    const releasedGroupIds = new Set(releases.map(r => r.groupId));
    const unreleasedGroups = allGroups.filter(g => !releasedGroupIds.has(g.id));

    // ── Render: batch list ────────────────────────────────────────────────────

        if (activeView === null) {
                return (
                    <>
                        {showReviewGalleryForm && (
                            <div style={{ padding: 'var(--space-6) var(--space-6) 0' }}>
                                <p className='review-gallery-hint'>
                                    Check original batches in the list below to include them.
                                    Artists will have 24 hours to request non-selected photos for further review.
                                </p>
                                <ReviewGalleryOpenForm
                                    selectedBatchIds={reviewBatchIds}
                                    onWindowOpened={() => {
                                        refreshBatches();
                                        setShowReviewGalleryForm(false);
                                        setReviewBatchIds(new Set());
                                    }}
                                    onCancel={() => {
                                        setShowReviewGalleryForm(false);
                                        setReviewBatchIds(new Set());
                                    }}
                                />
                            </div>
                        )}

                        <BatchListView
                            batches={batches}
                            navigate={navigate}
                            refreshBatches={refreshBatches}
                            onSelectBatch={handleSelectBatch}
                            extraHeaderAction={
                                !showReviewGalleryForm && !hasActiveSecondaryWindow && batches.length > 0 ? (
                                    <button
                                        className='user-submit-btn user-submit-btn--secondary'
                                        onClick={() => setShowReviewGalleryForm(true)}
                                    >
                                        Release Full Gallery
                                    </button>
                                ) : undefined
                            }
                            hideUploadButton={showReviewGalleryForm}
                            reviewSelectionMode={showReviewGalleryForm}
                            reviewBatchIds={reviewBatchIds}
                            onToggleReviewBatch={(id) => setReviewBatchIds(prev => {
                                const next = new Set(prev);
                                if (next.has(id)) next.delete(id); else next.add(id);
                                return next;
                            })}
                        />
                    </>
                );
            }

    if (activeView === 'secondary-review') {
        return (
            <SecondaryBatchDetail
                batchId={batchId}
                onBack={handleBack}
            />
        );
    }

const { label: batchStatusLabel, cls: batchStatusCls } = selectedBatch
    ? getStatusBadge(selectedBatch)
    : { label: '', cls: '' };

// Derived locally so the badge reacts immediately to tag changes
const allPhotosTaggedLocally = photos.length > 0
    && photos.every(p => tagMap.has(p.id) || p.isGroupPhoto);
const { label: taggingBadgeLabel, cls: taggingBadgeCls } = selectedBatch
    ? getStatusBadge({ ...selectedBatch, allPhotosTagged: allPhotosTaggedLocally })
    : { label: '', cls: '' };

    // ── Render: approval view ─────────────────────────────────────────────────

    if (activeView === 'approval') {
        return (
            <div className='approval-status'>
                <div className='approval-status-header'>
                    <button className='back-link' onClick={handleBack}>← Batches</button>
                    {selectedBatch && (
                        <span className='photos-batch-info'>
                            {' '}{selectedBatch.galleryName} · {selectedBatch.year}{' '}
                        </span>
                    )}
                    {selectedBatch && (
                        <ApprovalTypeBadge approvalType={selectedBatch.approvalType} />
                    )}
                    {selectedBatch && (
                        <span className={`batch-status-badge ${batchStatusCls}`}>{batchStatusLabel}</span>
                    )}
                </div>
                {selectedBatch && (
                    <div className='batch-meta-row'>
                        <span>Uploaded {new Date(selectedBatch.createdAt).toLocaleDateString()}</span>
                        {selectedBatch.description && (
                            <span className='batch-meta-separator'>·</span>
                        )}
                        {selectedBatch.description && (
                            <span>{selectedBatch.description}</span>
                        )}
                    </div>
                )}

                {inlineError && <InlineErrorBanner message={inlineError} onDismiss={() => setInlineError('')} />}

                {exportingStatus && <ExportProgressBar progress={exportProgress} />}

                {selectedBatch && photos.length > 0 && (
                    <div className='approval-status-actions'>
                        {exportMode ? (
                            <>
                                <span className='export-mode-label'>
                                    {exportSelectedIds.size === 0 ? 'Click photos to select' : `${exportSelectedIds.size} selected`}
                                </span>
                                <button className='app-nav-btn app-nav-btn--secondary' onClick={() => {
                                    setExportMode(false);
                                    setExportSelectedIds(new Set());
                                }}>
                                    Cancel
                                </button>
                                <button
                                    className='app-nav-btn'
                                    onClick={handleExportSelected}
                                    disabled={exportSelectedIds.size === 0 || exportingStatus === 'selected'}
                                >
                                    {exportingStatus === 'selected' ? 'Preparing ZIP…' : `Export Selected (${exportSelectedIds.size})`}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className='app-nav-btn app-nav-btn--secondary' onClick={() => setExportMode(true)}>
                                    Select Photos to Export
                                </button>
                                {approvedCount > 0 && (
                                    <button className='app-nav-btn' onClick={() => handleExportByStatus('approved')} disabled={!!exportingStatus}>
                                        {exportingStatus === 'approved' ? 'Preparing ZIP…' : `Download Approved (${approvedCount})`}
                                    </button>
                                )}
                                {rejectedCount > 0 && (
                                    <button className='app-nav-btn app-nav-btn--secondary' onClick={() => handleExportByStatus('rejected')} disabled={!!exportingStatus}>
                                        {exportingStatus === 'rejected' ? 'Preparing ZIP…' : `Download Rejected (${rejectedCount})`}
                                    </button>
                                )}
                                {notSelectedCount > 0 && (
                                    <button className='app-nav-btn app-nav-btn--secondary' onClick={() => handleExportByStatus('not_selected')} disabled={!!exportingStatus}>
                                        {exportingStatus === 'not_selected' ? 'Preparing ZIP…' : `Download Not Selected (${notSelectedCount})`}
                                    </button>
                                )}
                                {selectedBatch?.approvalType === 'single_point' && selectedBatch.status === 'ad_selected' && (
                                    <button
                                        className='app-nav-btn'
                                        style={{ marginLeft: 'auto' }}
                                        onClick={handleMarkAsProcessed}
                                    >
                                        Mark as Processed
                                    </button>
                                )}
                                {selectedBatch?.status === 'pending' && (
                                    <button
                                        className={`app-nav-btn app-nav-btn--secondary${correctTagsMode ? ' active' : ''}`}
                                        style={{ marginLeft: 'auto' }}
                                        onClick={() => setCorrectTagsMode(m => !m)}
                                    >
                                        {correctTagsMode ? 'Done Correcting' : 'Correct Tags'}
                                    </button>
                                )}
                                {selectedBatch?.approvalType === 'single_point' && selectedBatch.status === 'processed' && unreleasedGroups.length > 0 && (
                                    <div className='release-btn-wrapper'>
                                        <button
                                            className='app-nav-btn'
                                            onClick={() => setReleaseDropdownOpen(o => !o)}
                                        >
                                            Release to Group ▾
                                        </button>
                                        {releaseDropdownOpen && (
                                            <div className='release-dropdown'>
                                                {unreleasedGroups.map(g => (
                                                    <button
                                                        key={g.id}
                                                        className='release-dropdown-item'
                                                        disabled={releasingGroupId === g.id}
                                                        onClick={() => handleRelease(g.id)}
                                                    >
                                                        {releasingGroupId === g.id ? 'Releasing…' : g.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {selectedBatch?.approvalType === 'single_point' && selectedBatch.status === 'processed' && releases.length > 0 && (
                    <div className='release-strip'>
                        <span className='release-strip-label'>Released to:</span>
                        {releases.map(r => (
                            <span key={r.id} className='release-chip'>
                                {r.groupName}
                                <button
                                    className='release-chip-revoke'
                                    title={`Revoke access for ${r.groupName}`}
                                    disabled={revokingGroupId === r.groupId}
                                    onClick={() => handleRevoke(r.groupId, r.groupName)}
                                >
                                    {revokingGroupId === r.groupId ? '…' : '×'}
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {loadState.status !== 'ready' && (
                    <StatusMessage state={loadState} onRetry={retryLoadApprovalPhotos} />
                )}
                {loadState.status === 'ready' && photos.length === 0 && (
                    <p>No photos in this batch yet.</p>
                )}
                {loadState.status === 'ready' && photos.length > 0 && (
                    <div className='photo-grid'>
                        {photos.map(photo => (
                            <ApprovalPhotoCard
                                key={photo.id}
                                photo={photo}
                                photoStatusMap={photoStatusMap}
                                photoCreditsMap={photoCreditsMap}
                                artistStatusMap={artistStatusMap}
                                batchIsCommitted={batchIsCommitted}
                                exportMode={exportMode}
                                isExportSelected={exportSelectedIds.has(photo.id)}
                                canCorrectTags={selectedBatch?.status === 'pending' && correctTagsMode}
                                onCorrectTags={setCorrectTagsPhoto}
                                onExportToggle={(id) => setExportSelectedIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(id)) next.delete(id); else next.add(id);
                                    return next;
                                })}
                            />
                        ))}
                    </div>
                )}

                {batchIsCommitted && selectedBatch && selectedBatch.approvalType !== 'single_point' && (
                    <ArtistStatusPanel artistStatus={artistStatus} onResend={handleResend} />
                )}


                {modal && <ConfirmModal {...modal} />}
                {correctTagsPhoto && (
                    <CorrectTagsModal
                        photo={correctTagsPhoto}
                        batchId={selectedBatch!.id}
                        onClose={() => setCorrectTagsPhoto(null)}
                        onSuccess={() => {
                            setCorrectTagsPhoto(null);
                            refreshPhotos();
                            fetchPhotoCredits();
                        }}
                    />
                )}
            </div>
        );
    }

    // ── Render: tagging view ──────────────────────────────────────────────────

    return (
        <div className='tag-photos-controls'>
            <div className='tag-photos-header'>
                <button className='back-link' onClick={handleBack}>← Batches</button>
                {selectedBatch && (
                    <span className='photos-batch-info'>
                        {' '}{selectedBatch.galleryName} · {selectedBatch.year}{' '}
                    </span>
                )}
                {selectedBatch && (
                    <ApprovalTypeBadge approvalType={selectedBatch.approvalType} />
                )}
                {selectedBatch && (
                    <span className={`batch-status-badge ${taggingBadgeCls}`}>{taggingBadgeLabel}</span>
                )}
            </div>

            {inlineError && <InlineErrorBanner message={inlineError} onDismiss={() => setInlineError('')} />}

            {selectedBatch?.adZeroSelection ? (
                <>
                    <div className='zero-selection-banner'>
                        <p className='zero-selection-message'>
                            Artistic Staff did not select any photos from this batch.
                            No photos will be sent to artists for approval.
                        </p>
                        <div className='zero-selection-actions'>
                            <button
                                className='app-nav-btn app-nav-btn--secondary'
                                onClick={() => handleExportByStatus('not_selected')}
                                disabled={!!exportingStatus}
                            >
                                {exportingStatus === 'not_selected' ? 'Preparing ZIP…' : `Export All Photos (${photos.length})`}
                            </button>
                            <button className='app-nav-btn' onClick={handleMarkAsProcessed}>
                                Mark as Processed
                            </button>
                        </div>
                    </div>
                    {photos.length > 0 && (
                        <div className='tag-photo-grid'>
                            {photos.map(photo => (
                                <TagPhotoCard
                                    key={photo.id}
                                    photo={photo}
                                    isChecked={false}
                                    taggedNames={null}
                                    onCheck={() => {}}
                                    onView={() => setActivePhotoId(photo.id)}
                                    notSelected={true}
                                />
                            ))}
                        </div>
                    )}
                </>
            ) : (
            <>
            {isCommitted && (
                <p className='tag-committed-notice'>
                    This batch has been committed and can no longer be tagged.
                </p>
            )}

            {!isCommitted && (
                <div className={`tag-artist-panel${tagPanelOpen ? '' : ' collapsed'}`}>
                    {tagPanelOpen && (
                        <div className='tag-recent-col'>
                                {tagHistory.length > 0 && (
                                    <>
                                        <span className='tag-history-label'>Recent:</span>
                                        <div className='tag-history-row'>
                                            {tagHistory.map(tag => {
                                                const tagArtistIds = tag.artistIds.map(a => String(a.id));
                                                const isSingle = tagArtistIds.length === 1;
                                                const isActive = isSingle
                                                    ? selectedArtists.includes(tagArtistIds[0])
                                                    : tagArtistIds.length === selectedArtists.length &&
                                                      tagArtistIds.every(id => selectedArtists.includes(id));
                                                const handleClick = () => {
                                                    if (isSingle) {
                                                        const id = tagArtistIds[0];
                                                        setSelectedArtists(prev =>
                                                            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
                                                        );
                                                    } else if (isActive) {
                                                        setSelectedArtists([]);
                                                    } else {
                                                        setSelectedArtists(tagArtistIds);
                                                    }
                                                    setArtistQuery('');
                                                };
                                                return (
                                                    <span key={tag.id} className={`tag-history-chip${isActive ? ' active' : ''}`}>
                                                        <button className='tag-history-btn' onClick={handleClick}>{tag.name}</button>
                                                        <button className='tag-history-delete' title='Remove this tag' onClick={() => handleDeleteTag(tag.id)}>×</button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                    )}

                    <div className='tag-right-section'>
                        <div className='tag-panel-top-row'>
                            {tagPanelOpen && (
                                <span className='tag-scope-indicator'>
                                    {taggingGroups.length === 0
                                        ? 'All artists'
                                        : taggingGroups.map(g => g.name).join(', ')}
                                </span>
                            )}
                            <button className='tag-panel-toggle' onClick={() => setTagPanelOpen(o => !o)}>
                                <span className='tag-panel-toggle-label'>Tagging {tagPanelOpen ? '▼' : '▲'}</span>
                            </button>
                        </div>
                        {tagPanelOpen && (<>
                                {(selectedArtists.length > 0 || selectedIds.size > 0) && (
                                    <div className='tag-row-1'>
                                        {selectedArtists.length > 0 && (
                                            <div className='tag-selected-row'>
                                                {selectedArtists.map(id => {
                                                    const artist = artists.find(a => String(a.id) === id);
                                                    return artist ? (
                                                        <label key={id} className='artist-check-row selected'>
                                                            <input type='checkbox' checked={true} onChange={() => toggleArtist(id, false)} />
                                                            {artist.fName} {artist.lName}
                                                        </label>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}
                                        <div className='tag-action-row'>
                                            <div className='tag-primary-row'>
                                                {selectedIds.size > 0 && (
                                                    <button className='tag-save-btn tag-group-btn' onClick={handleMarkAsGroup}>
                                                        Tag as Group
                                                    </button>
                                                )}
                                                {selectedIds.size > 0 && selectedArtists.length > 0 && (
                                                    <button className='tag-save-btn' onClick={handleSaveTags}>
                                                        Tag {selectedIds.size} Photo{selectedIds.size !== 1 ? 's' : ''} — {selectedArtists.length} Artist{selectedArtists.length !== 1 ? 's' : ''} Selected
                                                    </button>
                                                )}
                                                {photos.length > 0 && (
                                                    <button className='ai-tag-panel-btn' onClick={handleStartAutoTag}>
                                                        <SparklesIcon />
                                                        Auto Tag with AI
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className='tag-row-2'>
                                    <div className='tag-search-wrapper'>
                                        <div className='tag-search-row'>
                                            <input
                                                type='search'
                                                className='artist-search'
                                                placeholder='Search artists…'
                                                value={artistQuery}
                                                onChange={e => setArtistQuery(e.target.value)}
                                            />
                                            {saveStatus === 'saved' && (
                                                <div className='tag-save-confirmation'>✓ Tags saved</div>
                                            )}
                                        </div>
                                        {artistQuery && (
                                            <div className='tag-search-dropdown'>
                                                {filteredArtists.length === 0
                                                    ? <span className='artist-no-results'>No artists found</span>
                                                    : unselectedMatches.map(a => (
                                                        <label key={a.id} className='artist-check-row'>
                                                            <input type='checkbox' checked={false} onChange={e => toggleArtist(a.id, e.target.checked)} />
                                                            {a.fName} {a.lName}
                                                        </label>
                                                    ))
                                                }
                                            </div>
                                        )}
                                    </div>
                                    {selectedArtists.length > 0 && (
                                        <div className='tag-name-group'>
                                            <input
                                                type='text'
                                                className='tag-name-input'
                                                placeholder='Save as named tag…'
                                                value={tagName}
                                                onChange={e => setTagName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleSaveTag()}
                                            />
                                            <button
                                                className='tag-save-tag-btn'
                                                onClick={handleSaveTag}
                                                disabled={savingTag || !tagName.trim()}
                                            >
                                                {savingTag ? 'Saving…' : 'Save Tag'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className='tag-row-3'>
                                    <div className='tag-utility-row'>
                                        {!allVisibleSelected && (
                                            <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set(visiblePhotos.map(p => p.id)))}>
                                                Select All ({visiblePhotos.length})
                                            </button>
                                        )}
                                        {selectedIds.size > 0 && (
                                            <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set())}>
                                                Clear Selection ({selectedIds.size})
                                            </button>
                                        )}
                                        {someSelectedHaveTags && (
                                            <button className='tag-clear-btn' onClick={handleClearTags}>Clear Tags</button>
                                        )}
                                    </div>
                                    <div className='tag-filter-inline'>
                                        <button className={filter === 'all' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('all')}>
                                            All ({photos.length})
                                        </button>
                                        <button className={filter === 'untagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('untagged')}>
                                            Untagged ({untaggedCount})
                                        </button>
                                        <button className={filter === 'tagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('tagged')}>
                                            Tagged ({taggedCount})
                                        </button>
                                    </div>
                                </div>
                        </>)}
                    </div>
                </div>
            )}

            {!isCommitted && (
                <div className='tag-filter-tabs'>
                    <button className={filter === 'all' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('all')}>All ({photos.length})</button>
                    <button className={filter === 'untagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('untagged')}>Untagged ({untaggedCount})</button>
                    <button className={filter === 'tagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('tagged')}>Tagged ({taggedCount})</button>
                </div>
            )}

            {!isCommitted && (
                <div className='tag-grid-utility-row'>
                    {!allVisibleSelected && (
                        <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set(visiblePhotos.map(p => p.id)))}>Select All ({visiblePhotos.length})</button>
                    )}
                    {selectedIds.size > 0 && (
                        <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set())}>Clear Selection ({selectedIds.size})</button>
                    )}
                    {someSelectedHaveTags && (
                        <button className='tag-clear-btn' onClick={handleClearTags}>Clear Tags</button>
                    )}
                </div>
            )}

            {!isCommitted && photos.length > 0 && (
                <div className='ai-tag-btn-wrapper'>
                    <button className='ai-tag-btn' onClick={handleStartAutoTag}>
                        <SparklesIcon />
                        Auto Tag with AI
                    </button>
                </div>
            )}

            {allTagged && !isCommitted && (
                <div className='tag-all-tagged'>
                    <span>✓ All photos tagged.</span>
                    <button className='tag-commit-btn' onClick={handleCommit} disabled={committing}>
                        {committing ? 'Committing…' : 'Commit Batch'}
                    </button>
                </div>
            )}

            {visiblePhotos.length > 0 && (
                <div className='tag-photo-grid'>
                    {visiblePhotos.map(photo => (
                        <TagPhotoCard
                            key={photo.id}
                            photo={photo}
                            isChecked={selectedIds.has(photo.id)}
                            taggedNames={tagMap.get(photo.id) ?? null}
                            onCheck={() => togglePhoto(photo.id)}
                            onView={() => setActivePhotoId(photo.id)}
                        />
                    ))}
                </div>
            )}

            {photos.length > 0 && visiblePhotos.length === 0 && (
                <p className='tag-empty-filter'>No photos match this filter.</p>
            )}

            
             </>
            )}

            <FullScreenView
                photo={activePhoto}
                onClose={() => setActivePhotoId(null)}
                onPrev={() => activeIdx > 0 && setActivePhotoId(photos[activeIdx - 1].id)}
                onNext={() => activeIdx < photos.length - 1 && setActivePhotoId(photos[activeIdx + 1].id)}
                hasPrev={activeIdx > 0}
                hasNext={activeIdx !== -1 && activeIdx < photos.length - 1}
                actionArea={
                    activePhoto && !isCommitted && !selectedBatch?.adZeroSelection ? (
                        <FullScreenTagPanel
                            key={activePhoto.id}
                            photo={activePhoto}
                            artists={taggableArtists}
                            tagHistory={tagHistory}
                            onSaveTags={handleFsSaveTags}
                            onMarkAsGroup={handleFsMarkAsGroup}
                            onSaveNamedTag={handleFsSaveNamedTag}
                            onDeleteTag={handleDeleteTag}
                            onConfirmClear={handleFsConfirmClear}
                        />
                    ) : null
                }
            />

            {autoTag?.open && (
                <AutoTagModal
                    state={autoTag}
                    showCancelConfirm={showCancelConfirm}
                    onCancelClick={handleCancelClick}
                    onCancelConfirm={handleCancelConfirm}
                    onCancelDeny={handleCancelDeny}
                    onClose={handleCloseModal}
                />
            )}

            {modal && <ConfirmModal {...modal} />}
        </div>
    );
}